// The daily close-out report: printed when the drawer is closed at the end of the day, or from
// Reports for any day. On the store's receipt printer (PrintNode) when it's set up; otherwise from
// this browser, 80 mm like every receipt.
import { closeoutWords } from '../../shared/industries';
import { useState } from 'react';
import { get } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { formatCents } from '../../shared/money';
import { formatDateTime, formatTime, zone } from '../../shared/format';
import { Button, Field, Sheet, Spinner, useToast } from '../ui/kit';
import { usePrint, type PrintFn } from '../ui/print';
import { useCloudPrint } from '../lib/printing';
import { ReceiptMark } from '../ui/legal';
import { ReceiptHeader, type StoreInfo } from './Receipt';

interface RepairRow { ticket: string; customer: string; device: string; work: string | null; price_cents: number | null }
export interface DailyReport {
  day: string; timezone: string; business_name: string; logo_url: string; store: StoreInfo;
  /** The kind of shop (older answers: a phone shop). */
  industry?: string;
  printed_by: string | null; printed_at: number; closed_by: string[]; still_open: boolean;
  /** The short close-out: the day in a few numbers (cost and profit on the owner's copy only; missing on older answers). */
  simple?: { sales_cents: number; cash_cents: number; card_cents: number; other_cents: number; tax_cents: number; repairs: { count: number; cents: number }; owner: { cost_cents: number; profit_cents: number } | null };
  money: { sales_count: number; collected_cents: number; tax_cents: number; refunds_count: number; refunds_cents: number; net_cents: number;
    by_method: { method: string; label: string; taken_cents: number; refunded_cents: number; net_cents: number }[];
    /** Sales a manager took the tax off: how many, the tax that was, and each one (why, and who OK'd it). */
    tax_removed?: { count: number; cents: number; sales: { receipt_no: number; reason: string; cents: number; by: string }[] } };
  sold: { phones: { count: number; cents: number }; repairs_paid_cents: number; orders_paid_cents: number; accessories: { count: number; cents: number } };
  drawers: { register: string | null; opened_by: string; opened_at: number; opening_cents: number | null; cash_sales_cents: number; cash_refunds_cents: number; buybacks_cents: number;
    /** Cash wholesale buyers paid into this drawer. */
    wholesale_cents?: number;
    paid_in_cents: number; paid_out_cents: number; expected_cents: number | null; counted_cents: number | null; difference_cents: number | null; hidden?: boolean; open: boolean; closed_by: string | null; closed_at: number | null }[];
  repairs: { checked_in: RepairRow[]; repaired: RepairRow[]; picked_up: RepairRow[] };
  orders: { number: string; description: string; customer: string; price_cents: number; paid: boolean; eta: string | null }[];
  activations: { carrier: string; type: string | null; phone: string | null; fee_cents: number }[];
  bill_pay: { count: number; total_cents: number; fees_cents: number; rows: { carrier: string; account: string | null; amount_cents: number; fee_cents: number }[] };
  /** What was paid is the owner's once its day is over (null for everyone else then). */
  buybacks: { count: number; total_cents: number | null; rows: { number: string; phone: string; paid_cents: number | null; paid_by: string }[] };
  /** Phones sold to wholesale buyers that day, and how the buyers paid (the owner's: null for everyone else). */
  wholesale?: { count: number; total_cents: number; by_method: { method: string; label: string; count: number; cents: number }[]; rows: { phone: string; buyer: string; price_cents: number; paid_by: string }[] } | null;
  /** The owner's (null for everyone else). */
  expenses: { count: number; total_cents: number } | null;
  /** The day's profit and loss: the owner's (null for everyone else, and on older answers). */
  pnl?: {
    revenue_cents: number; store_sales_cents: number; wholesale_cents: number; cost_of_goods_cents: number; gross_profit_cents: number; gross_margin_pct: number | null;
    commissions_cents: number | null; expenses_cents: number; drawer_difference_cents: number; net_profit_cents: number; wholesale_uncosted: number;
  } | null;
  worked: { name: string; hours: number }[];
}

/** Today in the store's time zone, as YYYY-MM-DD. */
export function storeToday(tz?: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz || zone.current }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA', { timeZone: zone.current }).format(new Date());
  }
}

const longDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(Date.UTC(y, m - 1, d, 12)));
};
const diffText = (c: number | null) => (c === null ? '—' : c === 0 ? 'Balanced' : `${c > 0 ? 'Over' : 'Short'} ${formatCents(Math.abs(c))}`);

function Section({ title, right }: { title: string; right?: string }) {
  return <><hr /><div className="rl strong"><span>{title}</span><span>{right ?? ''}</span></div></>;
}
function Item({ left, right }: { left: string; right?: string }) {
  return <div className="rl dim dr-item"><span>{left}</span><span>{right ?? ''}</span></div>;
}

export function DailyReportPaper({ r }: { r: DailyReport }) {
  const w = closeoutWords(r.industry);
  const time = (ms: number | null) => (ms ? formatTime(ms, r.timezone) : '');
  const repairItem = (x: RepairRow, i: number, money = false) => <Item key={i} left={`${x.ticket} ${x.customer} · ${x.device}${x.work ? `: ${x.work}` : ''}`} right={money && x.price_cents ? formatCents(x.price_cents) : ''} />;
  const hours = r.worked.reduce((s, w) => s + w.hours, 0);
  return (
    <div className="receipt daily-report" aria-label={`Daily close-out ${r.day}`}>
      <ReceiptHeader logo={r.logo_url} business={r.business_name} store={r.store} />
      <div className="order-banner">DAILY CLOSE-OUT</div>
      <div className="c strong">{longDay(r.day)}</div>
      <div className="c">{r.store.name} store</div>
      <div className="c strong">{r.closed_by.length ? `Closed by ${r.closed_by.join(', ')}` : 'The drawer isn’t closed yet'}</div>

      <Section title="MONEY IN" right={`${r.money.sales_count} sale${r.money.sales_count === 1 ? '' : 's'}`} />
      {r.money.by_method.map((m) => (
        <div key={m.method}>
          <div className="rl strong"><span>{m.label}</span><span>{formatCents(m.net_cents)}</span></div>
          {m.refunded_cents ? <Item left={`${formatCents(m.taken_cents)} taken, ${formatCents(m.refunded_cents)} refunded`} /> : null}
        </div>
      ))}
      {r.money.refunds_count ? <div className="rl"><span>Refunds ({r.money.refunds_count})</span><span>−{formatCents(r.money.refunds_cents)}</span></div> : null}
      <div className="rl tot"><span>TOTAL</span><span>{formatCents(r.money.net_cents)}</span></div>
      <div className="rl dim"><span>Sales tax in it</span><span>{formatCents(r.money.tax_cents)}</span></div>
      {r.money.tax_removed?.count ? (
        <>
          <div className="rl"><span>Tax removed ({r.money.tax_removed.count} sale{r.money.tax_removed.count === 1 ? '' : 's'})</span><span>{formatCents(r.money.tax_removed.cents)}</span></div>
          {r.money.tax_removed.sales.map((x) => <Item key={x.receipt_no} left={`#${x.receipt_no} ${x.reason} · ${x.by}`} right={formatCents(x.cents)} />)}
        </>
      ) : null}

      {r.drawers.map((d, i) => d.hidden ? (
        <div key={i}>
          <Section title={`CASH DRAWER${d.register ? ` · ${d.register}` : ''}`} right={d.open ? 'STILL OPEN' : ''} />
          <div className="dim dr-item">Opened {time(d.opened_at)} by {d.opened_by}</div>
          {!d.open ? <div className="strong dr-item">Closed {time(d.closed_at)} by {d.closed_by}</div> : null}
        </div>
      ) : (
        <div key={i}>
          <Section title={`CASH DRAWER${d.register ? ` · ${d.register}` : ''}`} right={d.open ? 'STILL OPEN' : ''} />
          <div className="rl dim"><span>Opened {time(d.opened_at)} by {d.opened_by}</span><span>{formatCents(d.opening_cents ?? 0)}</span></div>
          {d.cash_sales_cents ? <div className="rl"><span>Cash sales</span><span>{formatCents(d.cash_sales_cents)}</span></div> : null}
          {d.cash_refunds_cents ? <div className="rl"><span>Cash refunds</span><span>{formatCents(d.cash_refunds_cents)}</span></div> : null}
          {d.buybacks_cents ? <div className="rl"><span>Phones bought (cash)</span><span>{formatCents(d.buybacks_cents)}</span></div> : null}
          {d.wholesale_cents ? <div className="rl"><span>Wholesale (cash)</span><span>{formatCents(d.wholesale_cents)}</span></div> : null}
          {d.paid_in_cents ? <div className="rl"><span>Cash added</span><span>{formatCents(d.paid_in_cents)}</span></div> : null}
          {d.paid_out_cents ? <div className="rl"><span>Cash taken out</span><span>{formatCents(d.paid_out_cents)}</span></div> : null}
          <div className="rl"><span>{d.open ? 'Expected now' : 'Expected'}</span><span>{formatCents(d.expected_cents ?? 0)}</span></div>
          {!d.open ? (
            <>
              <div className="rl strong"><span>Counted</span><span>{formatCents(d.counted_cents ?? 0)}</span></div>
              <div className="rl strong"><span>Difference</span><span>{diffText(d.difference_cents)}</span></div>
              <div className="strong dr-item">Closed {time(d.closed_at)} by {d.closed_by}</div>
            </>
          ) : null}
        </div>
      ))}
      {!r.drawers.length ? <><Section title="CASH DRAWER" /><Item left="Not opened this day." /></> : null}

      <Section title={w.inFor} right={String(r.repairs.checked_in.length)} />
      {r.repairs.checked_in.map((x, i) => repairItem(x, i))}
      <Section title={w.ready} right={String(r.repairs.repaired.length)} />
      {r.repairs.repaired.map((x, i) => repairItem(x, i))}
      <Section title={w.pickedUp} right={String(r.repairs.picked_up.length)} />
      {r.repairs.picked_up.map((x, i) => repairItem(x, i, true))}
      {r.sold.repairs_paid_cents ? <div className="rl dim"><span>{w.paidToday}</span><span>{formatCents(r.sold.repairs_paid_cents)}</span></div> : null}

      <Section title="PARTS ORDERED" right={String(r.orders.length)} />
      {r.orders.map((o) => <Item key={o.number} left={`${o.number} ${o.customer}: ${o.description}${o.paid ? ' (paid)' : ''}`} right={formatCents(o.price_cents)} />)}

      {w.phones || r.activations.length ? (
        <>
          <Section title="ACTIVATIONS" right={String(r.activations.length)} />
          {r.activations.map((a, i) => <Item key={i} left={[a.carrier, a.type, a.phone].filter(Boolean).join(' · ')} right={a.fee_cents ? formatCents(a.fee_cents) : ''} />)}
        </>
      ) : null}

      {w.phones || r.bill_pay.count ? (
        <>
          <Section title="PHONE BILLS PAID" right={`${r.bill_pay.count} · ${formatCents(r.bill_pay.total_cents)}`} />
          {r.bill_pay.rows.map((b, i) => <Item key={i} left={[b.carrier, b.account].filter(Boolean).join(' ')} right={formatCents(b.amount_cents)} />)}
          {r.bill_pay.fees_cents ? <div className="rl dim"><span>Bill pay fees (ours)</span><span>{formatCents(r.bill_pay.fees_cents)}</span></div> : null}
        </>
      ) : null}

      {w.phones || r.buybacks.count ? (
        <>
          <Section title="PHONES BOUGHT" right={r.buybacks.total_cents === null ? String(r.buybacks.count) : `${r.buybacks.count} · ${formatCents(r.buybacks.total_cents)}`} />
          {r.buybacks.rows.map((b) => <Item key={b.number} left={`${b.number} ${b.phone} (${b.paid_by})`} right={b.paid_cents === null ? '' : formatCents(b.paid_cents)} />)}
        </>
      ) : null}

      {r.wholesale?.count ? (
        <>
          <Section title="SOLD WHOLESALE" right={`${r.wholesale.count} · ${formatCents(r.wholesale.total_cents)}`} />
          {r.wholesale.rows.map((w, i) => <Item key={i} left={`${w.phone} to ${w.buyer} (${w.paid_by})`} right={formatCents(w.price_cents)} />)}
          {r.wholesale.by_method.map((m) => <div key={m.method} className="rl dim"><span>{m.label} ({m.count})</span><span>{formatCents(m.cents)}</span></div>)}
        </>
      ) : null}

      <Section title="SOLD" />
      {w.phones || r.sold.phones.count ? <div className="rl"><span>Phones ({r.sold.phones.count})</span><span>{formatCents(r.sold.phones.cents)}</span></div> : null}
      <div className="rl"><span>Accessories and items ({r.sold.accessories.count})</span><span>{formatCents(r.sold.accessories.cents)}</span></div>
      {r.sold.orders_paid_cents ? <div className="rl"><span>Special orders paid</span><span>{formatCents(r.sold.orders_paid_cents)}</span></div> : null}
      {r.expenses?.count ? <div className="rl"><span>Expenses ({r.expenses.count})</span><span>{formatCents(r.expenses.total_cents)}</span></div> : null}

      {r.pnl ? (
        <>
          <Section title="PROFIT AND LOSS" right={r.pnl.net_profit_cents < 0 ? `Loss ${formatCents(-r.pnl.net_profit_cents)}` : `Profit ${formatCents(r.pnl.net_profit_cents)}`} />
          <div className="rl"><span>Revenue (after refunds, before tax)</span><span>{formatCents(r.pnl.revenue_cents)}</span></div>
          {r.pnl.wholesale_cents ? <Item left={`${formatCents(r.pnl.store_sales_cents)} store sales + ${formatCents(r.pnl.wholesale_cents)} wholesale`} /> : null}
          <div className="rl dr-cost"><span>Cost of goods</span><span>−{formatCents(r.pnl.cost_of_goods_cents)}</span></div>
          <div className={`rl strong ${r.pnl.gross_profit_cents < 0 ? 'dr-loss' : 'dr-profit'}`}><span>{r.pnl.gross_profit_cents < 0 ? 'Gross loss' : 'Gross profit'}{r.pnl.gross_margin_pct !== null ? ` (${r.pnl.gross_margin_pct}%)` : ''}</span><span>{formatCents(Math.abs(r.pnl.gross_profit_cents))}</span></div>
          {r.pnl.commissions_cents ? <div className="rl dr-profit"><span>Carrier commissions</span><span>{formatCents(r.pnl.commissions_cents)}</span></div> : null}
          {r.pnl.expenses_cents ? <div className="rl dr-cost"><span>Expenses</span><span>−{formatCents(r.pnl.expenses_cents)}</span></div> : null}
          {r.pnl.drawer_difference_cents ? <div className={`rl ${r.pnl.drawer_difference_cents < 0 ? 'dr-cost' : 'dr-profit'}`}><span>Drawers {r.pnl.drawer_difference_cents < 0 ? 'short' : 'over'}</span><span>{r.pnl.drawer_difference_cents < 0 ? '−' : ''}{formatCents(Math.abs(r.pnl.drawer_difference_cents))}</span></div> : null}
          <div className={`rl tot ${r.pnl.net_profit_cents < 0 ? 'dr-loss' : 'dr-profit'}`}><span>{r.pnl.net_profit_cents < 0 ? 'NET LOSS' : 'NET PROFIT'}</span><span>{formatCents(Math.abs(r.pnl.net_profit_cents))}</span></div>
          {r.pnl.wholesale_uncosted ? <Item left={`${r.pnl.wholesale_uncosted} phone${r.pnl.wholesale_uncosted === 1 ? '' : 's'} sold wholesale with no cost on record (counted at $0.00 cost)`} /> : null}
        </>
      ) : null}

      <Section title="WHO WORKED" right={r.worked.length ? `${hours.toFixed(1)} h` : ''} />
      {r.worked.length ? r.worked.map((w) => <Item key={w.name} left={w.name} right={`${w.hours.toFixed(2)} h`} />) : <Item left="Nobody clocked in here" />}

      <hr />
      <div className="c dim">Printed {formatDateTime(r.printed_at, r.timezone)}{r.printed_by ? ` by ${r.printed_by}` : ''}</div>
      <ReceiptMark />
    </div>
  );
}

/**
 * The short close-out: the day in a few big numbers. Sales, cash, card, the sales tax, the repairs
 * picked up and what repairs brought in, each drawer's count, and on the owner's copy the cost and
 * the profit. The long report (DailyReportPaper) has everything else.
 */
export function DailySimplePaper({ r }: { r: DailyReport }) {
  const x = r.simple;
  if (!x) return <DailyReportPaper r={r} />;
  const w = closeoutWords(r.industry);
  return (
    <div className="receipt daily-report daily-simple" aria-label={`Daily close-out ${r.day}`}>
      <ReceiptHeader logo={r.logo_url} business={r.business_name} store={r.store} />
      <div className="order-banner">DAILY CLOSE-OUT</div>
      <div className="c strong">{longDay(r.day)}</div>
      <div className="c">{r.store.name} store</div>
      <div className="c strong">{r.closed_by.length ? `Closed by ${r.closed_by.join(', ')}` : 'The drawer isn’t closed yet'}</div>
      <hr />
      <div className="rl tot"><span>SALES</span><span>{formatCents(x.sales_cents)}</span></div>
      <div className="rl strong ds-cash"><span>Cash</span><span>{formatCents(x.cash_cents)}</span></div>
      <div className="rl strong ds-card"><span>Card</span><span>{formatCents(x.card_cents)}</span></div>
      {x.other_cents ? <div className="rl strong"><span>Other</span><span>{formatCents(x.other_cents)}</span></div> : null}
      <div className="rl"><span>Sales tax</span><span>{formatCents(x.tax_cents)}</span></div>
      {r.money.refunds_count ? <div className="rl dim"><span>Refunds ({r.money.refunds_count})</span><span>−{formatCents(r.money.refunds_cents)}</span></div> : null}
      <hr />
      <div className="rl strong"><span>{w.pickedUp} ({x.repairs.count})</span><span>{formatCents(x.repairs.cents)}</span></div>
      {x.owner ? (
        <>
          <hr />
          <div className="rl strong dr-cost"><span>COST</span><span>{formatCents(x.owner.cost_cents)}</span></div>
          <div className={`rl tot ${x.owner.profit_cents < 0 ? 'dr-loss' : 'dr-profit'}`}><span>{x.owner.profit_cents < 0 ? 'LOSS' : 'PROFIT'}</span><span>{formatCents(Math.abs(x.owner.profit_cents))}</span></div>
        </>
      ) : null}
      <hr />
      {r.drawers.map((d, i) => (
        <div key={i} className="rl strong"><span>Drawer{d.register ? ` · ${d.register}` : ''}</span><span>{d.open ? 'STILL OPEN' : d.hidden ? `Closed by ${d.closed_by ?? ''}` : diffText(d.difference_cents)}</span></div>
      ))}
      {!r.drawers.length ? <div className="dim">Drawer not opened this day.</div> : null}
      <hr />
      <div className="c dim">Printed {formatDateTime(r.printed_at, r.timezone)}{r.printed_by ? ` by ${r.printed_by}` : ''}</div>
      <ReceiptMark />
    </div>
  );
}

/** Prints a day's close-out (the short one, or the full report) on the store's receipt printer, or from this browser. */
export function useDailyReportPrint() {
  const print = usePrint();
  const cloud = useCloudPrint();
  const toast = useToast();
  return (day: string, full = false) => cloud.printOut({
    kind: full ? 'daily_report_full' : 'daily_report', refId: day, title: `Daily close-out ${day}${full ? ' (full)' : ''}`,
    browser: () => { printDailyReportHere(print, day, full).catch((e) => toast(e instanceof Error ? e.message : 'The daily report couldn’t print.', 'error')); },
  });
}

async function printDailyReportHere(print: PrintFn, day: string, full: boolean) {
  const r = await get<DailyReport>(`/api/reports/daily?date=${encodeURIComponent(day)}`);
  print(full ? <DailyReportPaper r={r} /> : <DailySimplePaper r={r} />, 'thermal');
}

/** Reports → Daily close-out: pick the day, see the short close-out (or the full report), print it. */
export function DailyReportSheet({ tz, onClose }: { tz?: string | null; onClose: () => void }) {
  const today = storeToday(tz);
  const [day, setDay] = useState(today);
  const [full, setFull] = useState(false);
  const r = useLoad<DailyReport>(() => get(`/api/reports/daily?date=${encodeURIComponent(day)}`), [day]);
  const printReport = useDailyReportPrint();
  return (
    <Sheet
      title="Daily close-out"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button variant="ghost" onClick={() => setFull(!full)} aria-pressed={full}>{full ? 'Simple' : 'Full report'}</Button>
          <Button variant="primary" icon="printer" disabled={!r.data} onClick={() => printReport(day, full)}>{full ? 'Print full' : 'Print'}</Button>
        </>
      }
    >
      <Field label="Day"><input className="input" type="date" max={today} value={day} onChange={(e) => { if (e.target.value) setDay(e.target.value); }} aria-label="Day" /></Field>
      {r.error ? <p className="error-text">{r.error.message}</p> : !r.data ? <Spinner /> : <div className="daily-preview">{full ? <DailyReportPaper r={r.data} /> : <DailySimplePaper r={r.data} />}</div>}
    </Sheet>
  );
}
